import { DSQConflitingMedication } from "@/Common/Api/Dsq/Dto/Responses/DSQConflictingMedications";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { PrescriptionPrintHistoryClient } from "@/Common/Api/Patient/PrescriptionPrintHistoryClient";
import { VigilanceTokenClient } from "@/Common/Api/VigilanceSante/VigilanceTokenClient";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { Guid } from "@/Common/Models/Guid";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FileTypePrescription } from "@/ExportableFile/Models/FileTypes/FileTypePrescription";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { VigilanceQueryViewerPopupHelper } from "@/Patient/Helpers/VigilanceQueryViewerPopupHelper";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { PrescriptionPrintHistory } from "@/Patient/Models/Medication/PrescriptionPrintHistory";
import { MedicationResponse } from "@/Patient/Models/Medication/ResponseModels/MedicationResponse";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientPrescriptionTabResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.MedfarDictionary_ResolveMedicationFromSearch, this.Resolvedmedication);
        this.RegisterOperation(ServerOperations.Server.Myle_Prescribe, this.Prescribe);
        this.RegisterOperation(ServerOperations.Server.MedfarDictionnary_GetVigilanceMonographie, this.MedfarDictionnary_GetVigilanceMonographie);
        this.RegisterOperation(ServerOperations.Server.MedfarDictionary_GetVigilanceQueryViewerData, this.openVigilanceQueryViewerPopup);

        this.RegisterOperation(ServerOperations.Server.MedfarDictionnary_GetVigilanceNumberOfAlerts, this.MedfarDictionnary_GetVigilanceNumberOfAlerts);
    }

    private MedfarDictionnary_GetVigilanceMonographie(response: ServerEvent): void {
        VigilanceQueryViewerPopupHelper.openVigilancePopupForMonograph(response.Data.MonographieCode);
    }

    private async Prescribe(response: ServerEvent, store: PatientStore): Promise<void> {
        // ensure we display current employee as contributor to current note
        store.updateContributorsEncountersData(response.PatientId, response.Data.EncounterId);
        store.PatientPrescriptionTabModels[response.PatientId] = store.PatientPrescriptionTabModels[response.PatientId].set("IsFavorite", false);
        store.PatientPrescriptionTabModels[response.PatientId] = store.PatientPrescriptionTabModels[response.PatientId].set("IsLoading", false);
        store.PatientPrescriptionTabModels[response.PatientId] = store.PatientPrescriptionTabModels[response.PatientId].set("generalInstructions", null);

        store.PatientPrescriptionTabModels[response.PatientId] = store.PatientPrescriptionTabModels[response.PatientId]
            .set("IsFavorite", false)
            .set("IsLoading", false);

        const currentPatientPrescriptionGenerealInstructions: Record<string, string> = store.PatientMedicalSummaryModels[response.PatientId].get("PrescriptionsGeneralInstructions");
        store.PatientMedicalSummaryModels[response.PatientId] = store.PatientMedicalSummaryModels[response.PatientId].set(
            "PrescriptionsGeneralInstructions",
            {
                ...currentPatientPrescriptionGenerealInstructions,
                [response.Data.PrescriptionId]: response.Data.Notes
            }
        );

        const status = response.Data.Status; // 0: Success_Myle, 1: Success_MyleDSQ, 2: Error_Myle, 3: Error_DSQ

        const IsFavorite: boolean = response.Data.IsFavorite;
        const sendFax: boolean = response.Data.SendFax;
        const printCopy: boolean = response.Data.PrintCopy;
        const prescriptionId: Guid = response.Data.PrescriptionId;
        const signCopy: boolean = response.Data.SignCopy;
        const prescribeDate: Date = response.Data.PrecribedDate;

        const Errors: ErrorMessage[] = response.Errors;
        const ConflictingMedications: DSQConflitingMedication[] = response.Data.ConflictingMedications;

        if (status == 2) {
            // No action is defined when the status is 2
        }
        else if (status == 3) {
            // DSQ error
            if (Errors && Errors.length > 0) {
                if (sessionStore.DSQPrescriptionError >= 3) {
                    sessionStore.DSQPrescriptionDisabled = true;

                    sessionStore.showPopup("L'envoi au DSQ a été désactivé car il y a eu plus de 3 erreurs.");
                }
                else {
                    sessionStore.DSQPrescriptionError = sessionStore.DSQPrescriptionError + 1;
                }
            }

            if (ConflictingMedications.length > 0) {
                // handle DSQ duplicate error
                let messages =
                    [
                        "Le DSQ a détecté des doublons potentiels pour les médicaments suivants :",
                        ""
                    ]
                        .concat(ConflictingMedications.map(x => "-" + x.MyleMedicationName + "\r\n"));

                sessionStore.showPopup({
                    content: messages,
                    showCancel: false,

                    okLabel: "Retourner à la prescription",

                    altButtonLabel: "Prescrire",
                    altButtonAction: () => sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.Prescribe, { dsqDuplicatesConfirmations: ConflictingMedications })
                });
            }

            // Here we reload so that the failed transaction shows as a pending DSQ Transaction in the Note.
            store.ActionsMedicationTab.ReloadMedications(response.PatientId);
        }
        else { // (status == 0 || status == 1) {
            // Clean up
            store.PatientPrescriptionTabModels[response.PatientId] = store.PatientPrescriptionTabModels[response.PatientId].set("MedicationModels", Immutable.List([]));
            store.PatientPrescriptionTabModels[response.PatientId] = store.PatientPrescriptionTabModels[response.PatientId].set("prescriptionId", GuidHelper.NewGuid()); // Get Ready for next prescription

            sessionStore.dispatch(
                LocalPatientOperations.UI.ChangeVerticalTabs,
                {
                    value: store.IsMedicalItemFromOutsideNote ? PatientTab.MedicalSummary : PatientTab.MedicalNote,
                    isPrescriptionTabActive: false
                },
                50
            );

            store.ActionsMedicationTab.ReloadMedications(response.PatientId);

            if (IsFavorite)
                cacheStore.requestReloadFavoritePrescription();

            if (prescriptionId && (sendFax || printCopy || signCopy)) {
                const exportableFile = new FileTypePrescription({
                    prescriptionId: prescriptionId,
                    description: cacheStore.GetString("PrescriptionLabel"),
                    date: LuxonDateTimeParser.parse(prescribeDate)?.toString() ?? "",
                    supervisorContactId: IsNullOrWhiteSpace(sessionStore.lockedSupervisorContactId) ? null : sessionStore.lockedSupervisorContactId
                });

                const canSign = sessionStore.canSignPrescription(sessionStore.getCurrentEmployee().ContactFacetId);

                const config: Partial<IFaxPrintExportableFileConfig> = {
                    exportableFile,
                    patientContactId: response.PatientId,
                    onApplyOverlaysCallback: null,
                    isSyncToPortalAfterEnabled: false,
                    syncToPortalAfterAction: null,
                    isSaveSignatureEnabled: canSign,
                    hideSignaturePinControl: !canSign,
                    sentFaxItemId: prescriptionId,
                    onSignButtonCallback: () => patientStore.ActionsMedicalNote.LoadSignedAuthorizations({
                        prescriptonIds: [
                            ...store.PatientMedicalSummaryModels[response.PatientId]
                                .get("Prescriptions")
                                .filter(x => x.EncounterId == response.Data.EncounterId)
                                .toArray()
                                .map(p => p.PrescriptionId),
                            prescriptionId
                        ]
                    }),
                    onPrintButtonCallback: async () => {
                        const addPrescriptionPrintHistoryResponse = await PrescriptionPrintHistoryClient.addPrescriptionPrintHistory(prescriptionId);

                        const prescriptionPrintHistory = new PrescriptionPrintHistory(addPrescriptionPrintHistoryResponse.data);
                        patientStore.addPrescriptionPrintHistory(prescriptionPrintHistory, patientStore.getCurrentPatientContactId());
                    }
                };

                if (sendFax) {
                    config.exportMode = ExportMode.Fax;
                    config.defaultContext = store.PatientModels[response.PatientId]?.PreferredPharmacy?.getPharmacyInfo();
                }
                else if (printCopy) {
                    config.exportMode = ExportMode.Print;
                }
                else if (signCopy) {
                    const isPrescriptionSigned = await this.validatePinFrequencyBeforeSign(exportableFile);
                    if (isPrescriptionSigned)
                        return;

                    config.exportMode = ExportMode.Sign;
                }

                sessionStore.dispatchMain(
                    LocalOperations.MainComponentViewOperations.FaxPrintExportableFile,
                    { config }
                );
            }
        }
    }

    private Resolvedmedication(response: ServerEvent, store: PatientStore): void {
        const responseData: MedicationResponse = response.Data.FullMedicationDTO;
        let prescriptionTabModel = store.PatientPrescriptionTabModels[response.PatientId]; // Get Patient Model
        const medicationModels = prescriptionTabModel.get("MedicationModels"); // Get Old List

        var items = medicationModels.map((item: MedicationModel) => {
            if (item.Model.Id === responseData.Id) {
                item.FromResponseFullUpdate(responseData);
            }

            // apply global renewal number for new resolved medication
            if (prescriptionTabModel.get("updateAllRenewalsNumber"))
                item.SetRefillNumber(prescriptionTabModel.get("updateAllRenewalsNumber"));

            return item;
        });

        prescriptionTabModel = prescriptionTabModel.set("MedicationModels", items.toList()); // Set new List
        store.PatientPrescriptionTabModels[response.PatientId] = prescriptionTabModel; // Replace patient Model;
    }

    private MedfarDictionnary_GetVigilanceNumberOfAlerts(response: ServerEvent, store: PatientStore): void {
        let prescriptionTabModel = store.PatientPrescriptionTabModels[response.PatientId];
        const eventId = response.Data.EventId;
        const lastVigilanceAlertEvent = prescriptionTabModel.get("LastVigilanceAlertEvent");

        if (lastVigilanceAlertEvent == eventId || (lastVigilanceAlertEvent == null && eventId == "PrescriptionTabMount")) {
            prescriptionTabModel = prescriptionTabModel
                .set("VigilanceNumberOfAlerts", response.Data.NumberOfAlerts)
                .set("VigilanceSevereAlertLevel", response.Data.SevereAlertLevel)
                .set("VigilanceLastDatabaseUpdateDate", response.Data.VigilanceLastDatabaseUpdateDate);

            store.PatientPrescriptionTabModels[response.PatientId] = prescriptionTabModel;
        }
    }

    private async openVigilanceQueryViewerPopup(response: ServerEvent): Promise<void> {
        const vigilanceUrl = cacheStore.ClinicSettingsServices.VigilanceServiceV2;
        const token = await VigilanceTokenClient.getVigilanceTokenAsync();

        VigilanceQueryViewerPopupHelper.openVigilanceViewerPopup(vigilanceUrl, response.Data.PatientData, token);
    }

    private async validatePinFrequencyBeforeSign(exportableFile: ExportableFile): Promise<boolean> {
        if (!cacheStore.ClinicSettings.EnableQuickDocumentSigning)
            return false;

        const pinFrequencyModel = LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency);
        const pinFrequencyToken = pinFrequencyModel?.prescriptionToken;
        const isTokenValid = await ExportableFileHelper.validatePinFrequencyToken(pinFrequencyToken);

        if (isTokenValid) {
            await exportableFile.persistSignature();
            displayToast(MessageToast.Success, cacheStore.GetString("DocumentSigningPinOptions_SignedSuccessfullyLabel"));
        }

        return isTokenValid;
    }
}

export { PatientPrescriptionTabResponseHandler };
